/**
 * Platform API-driven cascading dropdown system for class registration.
 * Consumes the nested schedule data shape from the NTSS Platform public API
 * and populates the same form dropdowns + platform hidden fields.
 */
var Ntss;
(function (Ntss) {
    'use strict';
    // Module state
    let _scheduleData = null;
    let _counties = null;
    let _currentLanguageIsSpanish = false;
    // Tracks the in-flight counties fetch so a slow earlier request can't clobber the
    // result of a later one when the user changes the citation state quickly.
    let _countiesAbortController = null;
    // Store reason name for the currently selected reason (used by validation in traffic-school-registration.ts)
    let _currentReasonName = '';
    /**
     * Gets the current platform reason name (used by traffic-school-registration.ts for validation).
     * When platform data is active, reason values are string names, not GUIDs.
     */
    function getPlatformReasonName() {
        return _currentReasonName;
    }
    Ntss.getPlatformReasonName = getPlatformReasonName;
    /**
     * Initializes the platform cascading dropdown system.
     */
    function initPlatformCascadingDropdowns(data) {
        if (!data || !data.schedule || !data.schedule.classes) {
            console.error('Invalid platform schedule data structure', data);
            return;
        }
        _scheduleData = data.schedule;
        _counties = data.counties;
        const locationDropdown = document.getElementById('locationSelector');
        const courseDropdown = document.getElementById('courseSelector');
        const dateDropdown = document.getElementById('dateSelector');
        const reasonDropdown = document.getElementById('reasonSelector');
        if (!locationDropdown || !courseDropdown || !dateDropdown || !reasonDropdown) {
            console.error('One or more dropdown elements not found');
            return;
        }
        // Platform hidden fields
        const platformClassId = document.querySelector('input[name="FormInput.PlatformClassId"]');
        const platformCourseId = document.querySelector('input[name="FormInput.PlatformCourseId"]');
        const platformReasonForTaking = document.querySelector('input[name="FormInput.PlatformReasonForTaking"]');
        const platformCounty = document.querySelector('input[name="FormInput.PlatformCounty"]');
        // Populate locations
        populateLocationDropdown(locationDropdown, _scheduleData);
        // Populate county dropdown with platform string data
        populatePlatformCountyDropdown(_counties);
        // --- Cascade event listeners ---
        locationDropdown.addEventListener('change', function () {
            const locationKey = this.value;
            if (locationKey) {
                const option = this.options[this.selectedIndex];
                _currentLanguageIsSpanish = option.dataset.spanish === 'true';
                populateCourseDropdown(courseDropdown, locationKey, _scheduleData);
            }
            else {
                _currentLanguageIsSpanish = false;
                clearDropdownWithPlaceholder(courseDropdown, 'Select Course ...');
            }
            clearDropdownWithPlaceholder(dateDropdown, 'Select Class Date ...');
            clearDropdownWithPlaceholder(reasonDropdown, 'Select Reason ...');
            // Clear platform hidden fields
            if (platformClassId)
                platformClassId.value = '';
            if (platformCourseId)
                platformCourseId.value = '';
            if (platformReasonForTaking)
                platformReasonForTaking.value = '';
            _currentReasonName = '';
            triggerKoUpdate(courseDropdown);
            triggerKoUpdate(dateDropdown);
            triggerKoUpdate(reasonDropdown);
        });
        courseDropdown.addEventListener('change', function () {
            const courseTypeId = this.value;
            const locationKey = locationDropdown.value;
            if (courseTypeId && locationKey) {
                populateDateDropdown(dateDropdown, locationKey, courseTypeId, _scheduleData);
            }
            else {
                clearDropdownWithPlaceholder(dateDropdown, 'Select Class Date ...');
            }
            clearDropdownWithPlaceholder(reasonDropdown, 'Select Reason ...');
            if (platformClassId)
                platformClassId.value = '';
            if (platformCourseId)
                platformCourseId.value = '';
            if (platformReasonForTaking)
                platformReasonForTaking.value = '';
            _currentReasonName = '';
            triggerKoUpdate(dateDropdown);
            triggerKoUpdate(reasonDropdown);
        });
        dateDropdown.addEventListener('change', function () {
            const selectedOption = this.options[this.selectedIndex];
            if (selectedOption && selectedOption.value) {
                const classId = selectedOption.dataset.classId || '';
                const courseId = selectedOption.dataset.courseId || '';
                if (platformClassId)
                    platformClassId.value = classId;
                if (platformCourseId)
                    platformCourseId.value = courseId;
                populateReasonDropdown(reasonDropdown, classId, courseId, _scheduleData);
            }
            else {
                clearDropdownWithPlaceholder(reasonDropdown, 'Select Reason ...');
                if (platformClassId)
                    platformClassId.value = '';
                if (platformCourseId)
                    platformCourseId.value = '';
            }
            if (platformReasonForTaking)
                platformReasonForTaking.value = '';
            _currentReasonName = '';
            triggerKoUpdate(reasonDropdown);
        });
        reasonDropdown.addEventListener('change', function () {
            const reasonName = this.value;
            _currentReasonName = reasonName || '';
            if (platformReasonForTaking)
                platformReasonForTaking.value = reasonName || '';
        });
        // County dropdown change → set platform hidden field
        const countyDropdown = document.getElementById('citationCountyDropdown');
        if (countyDropdown && platformCounty) {
            countyDropdown.addEventListener('change', function () {
                platformCounty.value = this.value;
            });
        }
        const citationStateDropdown = document.getElementById('platformCitationStateDropdown');
        if (citationStateDropdown && countyDropdown) {
            citationStateDropdown.addEventListener('change', function () {
                const selectedState = this.value;
                if (!selectedState) {
                    populatePlatformCountyDropdown([]);
                    if (platformCounty)
                        platformCounty.value = '';
                    return;
                }
                fetchAndPopulateCounties(selectedState).catch(err => {
                    if ((err === null || err === void 0 ? void 0 : err.name) !== 'AbortError') {
                        console.error('Failed to fetch counties for state', selectedState, err);
                    }
                });
            });
        }
        restorePlatformSelections(locationDropdown, courseDropdown, dateDropdown, reasonDropdown, _scheduleData);
    }
    Ntss.initPlatformCascadingDropdowns = initPlatformCascadingDropdowns;
    async function fetchAndPopulateCounties(state) {
        // Cancel any previous in-flight request so its (stale) response can't overwrite
        // a newer state selection's counties.
        _countiesAbortController === null || _countiesAbortController === void 0 ? void 0 : _countiesAbortController.abort();
        const controller = new AbortController();
        _countiesAbortController = controller;
        const url = `?handler=Counties&state=${encodeURIComponent(state)}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: { 'Accept': 'application/json' },
            signal: controller.signal
        });
        if (!response.ok) {
            throw new Error(`Counties endpoint returned ${response.status}`);
        }
        const data = await response.json();
        // If another fetch started while we were awaiting, abandon this stale result.
        if (_countiesAbortController !== controller)
            return;
        _counties = data.counties || [];
        populatePlatformCountyDropdown(_counties);
        // Previously selected county likely doesn't exist in the new state.
        const countyDropdown = document.getElementById('citationCountyDropdown');
        const platformCountyInput = document.querySelector('input[name="FormInput.PlatformCounty"]');
        if (countyDropdown) {
            countyDropdown.value = '';
            triggerKoUpdate(countyDropdown);
        }
        if (platformCountyInput) {
            platformCountyInput.value = '';
        }
    }
    // --- Dropdown population functions ---
    function populateLocationDropdown(dropdown, data) {
        var _a;
        clearDropdownWithPlaceholder(dropdown, 'Select Location ...');
        // Build unique location entries from classes using the class-level language field
        const locationMap = new Map();
        for (const cls of data.classes) {
            const hasAvailableCourses = cls.courses.some(c => c.availableSeats > 0);
            if (!hasAvailableCourses)
                continue;
            const isSpanish = ((_a = cls.language) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === 'spanish';
            const hasDistinctLocation = !!cls.locationName
                && cls.locationName.toLowerCase() !== cls.campusName.toLowerCase();
            const combinedName = hasDistinctLocation
                ? `${cls.campusName} - ${cls.locationName}`
                : cls.campusName;
            const langSuffix = isSpanish ? 'es' : 'en';
            const key = `${cls.campusId}|${cls.locationId || ''}|${langSuffix}`;
            if (!locationMap.has(key)) {
                locationMap.set(key, {
                    key,
                    campusId: cls.campusId,
                    locationId: cls.locationId,
                    displayName: isSpanish ? `${combinedName} (en Español)` : combinedName,
                    isSpanish
                });
            }
        }
        const sorted = Array.from(locationMap.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
        for (const loc of sorted) {
            const option = document.createElement('option');
            option.value = loc.key;
            option.textContent = loc.displayName;
            option.dataset.campusId = loc.campusId;
            option.dataset.locationId = loc.locationId || '';
            option.dataset.spanish = String(loc.isSpanish);
            dropdown.appendChild(option);
        }
    }
    function populateCourseDropdown(dropdown, locationKey, data) {
        clearDropdownWithPlaceholder(dropdown, 'Select Course ...');
        const [campusId, locationId] = locationKey.split('|');
        // Find classes at this location with available courses
        const classesAtLocation = data.classes.filter(cls => cls.campusId === campusId &&
            (cls.locationId || '') === locationId);
        // Collect unique course types
        const courseMap = new Map();
        for (const cls of classesAtLocation) {
            for (const course of cls.courses) {
                if (course.availableSeats <= 0)
                    continue;
                if (!courseMap.has(course.courseTypeId)) {
                    const name = (_currentLanguageIsSpanish && course.spanishDisplayName)
                        ? course.spanishDisplayName
                        : course.displayName;
                    courseMap.set(course.courseTypeId, name);
                }
            }
        }
        const sorted = Array.from(courseMap.entries()).sort((a, b) => a[1].localeCompare(b[1]));
        for (const [courseTypeId, name] of sorted) {
            const option = document.createElement('option');
            option.value = courseTypeId;
            option.textContent = name;
            dropdown.appendChild(option);
        }
    }
    function populateDateDropdown(dropdown, locationKey, courseTypeId, data) {
        clearDropdownWithPlaceholder(dropdown, 'Select Class Date ...');
        const [campusId, locationId] = locationKey.split('|');
        const locale = _currentLanguageIsSpanish ? 'es-ES' : 'en-US';
        const entries = [];
        for (const cls of data.classes) {
            if (cls.campusId !== campusId || (cls.locationId || '') !== locationId)
                continue;
            for (const course of cls.courses) {
                if (course.courseTypeId !== courseTypeId)
                    continue;
                if (course.availableSeats <= 0)
                    continue;
                const classDate = new Date(cls.date);
                if (classDate <= new Date())
                    continue;
                const dateStr = classDate.toLocaleDateString(locale, {
                    month: 'short', day: 'numeric', year: 'numeric'
                });
                const dayOfWeek = classDate.toLocaleDateString(locale, { weekday: 'long' });
                const seatsText = _currentLanguageIsSpanish
                    ? `${course.availableSeats} asientos disponibles`
                    : `${course.availableSeats} seats available`;
                const fromText = _currentLanguageIsSpanish ? 'de' : 'from';
                const toText = _currentLanguageIsSpanish ? 'a' : 'to';
                const displayText = `${dateStr} -- (${dayOfWeek} ${fromText} ${course.startTime} ${toText} ${course.endTime}) (${seatsText})`;
                entries.push({
                    classId: cls.classId,
                    courseId: course.courseId,
                    date: cls.date,
                    startTime: course.startTime,
                    endTime: course.endTime,
                    availableSeats: course.availableSeats,
                    displayText
                });
            }
        }
        entries.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        for (const entry of entries) {
            const option = document.createElement('option');
            // Use a composite value so we can parse it, but store real IDs in data attributes
            option.value = `${entry.classId}|${entry.courseId}`;
            option.textContent = entry.displayText;
            option.dataset.classId = entry.classId;
            option.dataset.courseId = entry.courseId;
            dropdown.appendChild(option);
        }
    }
    function populateReasonDropdown(dropdown, classId, courseId, data) {
        const placeholderText = _currentLanguageIsSpanish ? 'Seleccione Razón ...' : 'Select Reason ...';
        clearDropdownWithPlaceholder(dropdown, placeholderText);
        // Find the course in the schedule to get its enrollment reasons
        for (const cls of data.classes) {
            if (cls.classId !== classId)
                continue;
            for (const course of cls.courses) {
                if (course.courseId !== courseId)
                    continue;
                const reasons = course.enrollmentReasons
                    .map(r => ({
                    name: r.name,
                    displayName: (_currentLanguageIsSpanish && r.spanishName) ? r.spanishName : r.name
                }))
                    .sort((a, b) => a.displayName.localeCompare(b.displayName));
                for (const reason of reasons) {
                    const option = document.createElement('option');
                    // Value is the English name (sent to platform API as ReasonForTaking)
                    option.value = reason.name;
                    option.textContent = reason.displayName;
                    dropdown.appendChild(option);
                }
                return;
            }
        }
    }
    function populatePlatformCountyDropdown(counties) {
        const dropdown = document.getElementById('citationCountyDropdown');
        if (!dropdown)
            return;
        clearDropdownWithPlaceholder(dropdown, 'County where you received the Ticket');
        if (!counties || counties.length === 0)
            return;
        const sorted = [...counties].sort();
        for (const county of sorted) {
            const option = document.createElement('option');
            option.value = county;
            option.textContent = `${county} County`;
            dropdown.appendChild(option);
        }
        // Restore previous selection (only meaningful on initial paint with the server-rendered list)
        if (Ntss.previousSelections && Ntss.previousSelections.platformCounty) {
            const prev = Ntss.previousSelections.platformCounty;
            const matchOption = Array.from(dropdown.options).find(o => o.value === prev);
            if (matchOption) {
                dropdown.value = prev;
                triggerKoUpdate(dropdown);
            }
        }
    }
    function restorePlatformSelections(locationDropdown, courseDropdown, dateDropdown, reasonDropdown, data) {
        var _a;
        const prev = Ntss.previousSelections;
        if (!prev || !prev.platformClassId || !prev.platformCourseId)
            return;
        // Find the class and course to determine the location key
        for (const cls of data.classes) {
            if (cls.classId !== prev.platformClassId)
                continue;
            for (const course of cls.courses) {
                if (course.courseId !== prev.platformCourseId)
                    continue;
                // Determine language
                const isSpanish = ((_a = cls.language) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === 'spanish';
                const langSuffix = isSpanish ? 'es' : 'en';
                const locationKey = `${cls.campusId}|${cls.locationId || ''}|${langSuffix}`;
                // Restore location
                if (!setDropdownValueAndTrigger(locationDropdown, locationKey))
                    return;
                _currentLanguageIsSpanish = isSpanish;
                // Populate and restore course
                populateCourseDropdown(courseDropdown, locationKey, data);
                if (!setDropdownValueAndTrigger(courseDropdown, course.courseTypeId))
                    return;
                // Populate and restore date
                populateDateDropdown(dateDropdown, locationKey, course.courseTypeId, data);
                const dateValue = `${cls.classId}|${course.courseId}`;
                if (!setDropdownValueAndTrigger(dateDropdown, dateValue))
                    return;
                // Set platform hidden fields
                const platformClassInput = document.querySelector('input[name="FormInput.PlatformClassId"]');
                const platformCourseInput = document.querySelector('input[name="FormInput.PlatformCourseId"]');
                if (platformClassInput)
                    platformClassInput.value = cls.classId;
                if (platformCourseInput)
                    platformCourseInput.value = course.courseId;
                // Populate and restore reason
                populateReasonDropdown(reasonDropdown, cls.classId, course.courseId, data);
                if (prev.platformReasonForTaking) {
                    setDropdownValueAndTrigger(reasonDropdown, prev.platformReasonForTaking);
                    _currentReasonName = prev.platformReasonForTaking;
                    const platformReasonInput = document.querySelector('input[name="FormInput.PlatformReasonForTaking"]');
                    if (platformReasonInput)
                        platformReasonInput.value = prev.platformReasonForTaking;
                }
                return;
            }
        }
    }
    // --- Utility functions ---
    function clearDropdownWithPlaceholder(dropdown, placeholderText) {
        dropdown.innerHTML = '';
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = placeholderText;
        dropdown.appendChild(placeholder);
    }
    function triggerKoUpdate(element) {
        element.dispatchEvent(new Event('change', { bubbles: true }));
    }
    function setDropdownValueAndTrigger(dropdown, value) {
        const option = Array.from(dropdown.options).find(opt => opt.value === value);
        if (option) {
            dropdown.value = value;
            triggerKoUpdate(dropdown);
            return true;
        }
        return false;
    }
})(Ntss || (Ntss = {}));
